/**
 * 캠페인 문서 → 조회 조건.
 *
 * 캠페인 문서는 **같은 뜻을 여러 필드에 담고 있다** — 마켓이 `keywordFilter.markets` ·
 * `market` · `markets` 셋 중 아무 데나 있고, 계정이 문자열일 수도 객체일 수도 있다.
 * 그 흔들림을 여기서 한 번 흡수해서 위쪽은 한 가지 모양만 본다.
 *
 * ⚠️ 두 화면이 함께 쓴다 — 함부로 좁히지 마라. `sections/campaign` 과
 * `sections/dashboard-new`(시딩 수집 카드)가 같은 함수를 본다. **다시 복사하지 마라.**
 *
 * ⚠️ 함께 모으지 **않은** 것이 하나 있다 — `dashboard-new` 의
 * `buildSeedingCollectionQuery`(ES 조회)는 캠페인 화면의 조회와 **모양만 같고 결과가 다르다**.
 * 기간을 선택 기간과 교집합하고, 마켓·채널이 비었을 때 전역 필터를 안 걸고,
 * 토글 제외를 ES 에 싣는다. **합치면 두 화면이 다 바뀐다.**
 */

import { paths } from 'src/routes/paths';

/** Firestore Timestamp · Date · 문자열 무엇이 와도 Date 로. 못 읽으면 null */
export const toDateObj = (val: any): Date | null => {
  if (!val) return null;
  try {
    return typeof val?.toDate === 'function' ? val.toDate() : (val instanceof Date ? val : new Date(val));
  } catch {
    return null;
  }
};

/**
 * 순간 → **KST 달력** 날짜 `YYYYMMDD` — 프로세스 시간대와 무관하다(UTC 서버와 KST 브라우저가 같은 날을 짓는다).
 * @returns 못 읽으면 `null`
 */
export function toKstYmd(date: Date | string | number | null | undefined): string | null {
  if (date === null || date === undefined || date === '') return null;
  const ms = new Date(date).getTime();
  if (Number.isNaN(ms)) return null;
  const kst = new Date(ms + KST_OFFSET_MS);
  return `${kst.getUTCFullYear()}${String(kst.getUTCMonth() + 1).padStart(2, '0')}${String(kst.getUTCDate()).padStart(2, '0')}`;
}

/** KST = UTC+9(서머타임 없음) */
const KST_OFFSET_MS = 9 * 60 * 60 * 1000;

/** Date → ES `postDate` 가 쓰는 YYYYMMDD 숫자 */
export const formatDateToNumber = (date: Date | string | number | null | undefined): number | null => {
  if (!date) return null;
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return parseInt(`${year}${month}${day}`, 10);
};

/**
 * ES 필터에 실을 마켓 코드 (대문자).
 * ⚠️ 「전체」·`ALL` 은 **빼 버린다** — 그것은 필터를 걸지 말라는 뜻이라 값으로 남기면 안 된다.
 */
export const getCampaignMarkets = (campaign: any): string[] => {
  const marketsRaw = Array.isArray(campaign?.keywordFilter?.markets)
    ? campaign.keywordFilter.markets
    : (Array.isArray(campaign?.market)
      ? campaign.market
      : (Array.isArray(campaign?.markets) ? campaign.markets : []));
  return (Array.isArray(marketsRaw) ? marketsRaw : [])
    .map((raw: unknown) => String(raw || '').trim().toUpperCase())
    .filter((value: string) => value.length > 0 && value !== 'ALL' && value !== '전체');
};

/** ES 필터에 실을 채널 id (소문자). 「전체」·`all` 은 위와 같은 이유로 뺀다 */
export const getCampaignPlatforms = (campaign: any): string[] => {
  const platformsRaw = Array.isArray(campaign?.keywordFilter?.platforms)
    ? campaign.keywordFilter.platforms
    : (Array.isArray(campaign?.platforms) ? campaign.platforms : []);
  return (Array.isArray(platformsRaw) ? platformsRaw : [])
    .map((raw: unknown) => String(raw || '').trim().toLowerCase())
    .filter((value: string) => value.length > 0 && value !== 'all' && value !== '전체');
};

/**
 * 캠페인 한 건의 조회 조건 한 벌 — 키워드 · 마켓 · 채널 · 계정 · 기간.
 *
 * ⚠️ **마켓을 소문자로 준다.** 위의 `getCampaignMarkets`(대문자)와 다르다 —
 * 이쪽은 브라우저 후필터(`filterPostsByCampaignCriteria`)가 쓰고 저쪽은 ES 가 쓴다.
 * 합치지 마라. 대소문자를 맞추면 한쪽이 아무것도 못 거른다.
 * ⚠️ 끝나는 날이 없으면 **오늘**로 본다 — 진행 중인 캠페인이라는 뜻이다.
 */
export const buildCampaignCriteria = (campaign: any, toDate: (val: any) => Date | null = toDateObj) => {
  if (!campaign) {
    return {
      keywords: [],
      markets: [],
      platforms: [],
      accounts: [],
      period: { start: null, end: null },
    };
  }

  const keywords = (Array.isArray(campaign?.keywords) ? campaign.keywords : [])
    .map((t: unknown) => String(t || '').trim())
    .filter((t: string) => t.length > 0);

  const marketsRaw = Array.isArray(campaign?.keywordFilter?.markets)
    ? campaign.keywordFilter.markets
    : (Array.isArray(campaign?.market)
      ? campaign.market
      : (Array.isArray(campaign?.markets) ? campaign.markets : []));
  const markets = (Array.isArray(marketsRaw) ? marketsRaw : [])
    .map((t: unknown) => String(t || '').trim().toLowerCase())
    .filter((t: string) => t.length > 0);

  const platforms = (Array.isArray(campaign?.platforms) ? campaign.platforms : [])
    .map((t: unknown) => String(t || '').trim().toLowerCase())
    .filter((t: string) => t.length > 0);

  let accounts: any[] = [];
  if (Array.isArray(campaign?.keywordFilter?.accounts)) {
    accounts = campaign.keywordFilter.accounts;
  } else if (Array.isArray(campaign?.accounts)) {
    accounts = campaign.accounts
      .map((acc: any) => {
        if (acc && typeof acc === 'object') {
          const platform = String(acc?.platform || acc?.platformId || '').trim().toLowerCase();
          const account = String(acc?.account || acc?.username || '').trim();
          const budget = Number(acc?.budget) || 0;
          if (platform && account) {
            return { platform, account, budget };
          }
          return acc?.account || acc?.username || acc;
        }
        return acc;
      })
      .filter((acc: any) => {
        if (typeof acc === 'object' && acc !== null) {
          return acc.account && String(acc.account).trim().length > 0;
        }
        return acc && String(acc).trim().length > 0;
      });
  }

  const startDate = toDate(campaign?.period?.start);
  const endDate = toDate(campaign?.period?.end) || new Date();

  return {
    keywords,
    markets,
    platforms,
    accounts,
    period: { start: startDate, end: endDate },
  };
};

/**
 * postDate(YYYYMMDD)와 기간으로 포스트를 거른다.
 * ⚠️ **날짜만 비교한다**(시:분:초를 0으로 내린다). 경계일의 글이 시간 때문에 빠지면 안 된다.
 * ⚠️ `postDate` 가 없거나 8자리가 아니면 **버린다** — 기간 안이라고 볼 근거가 없다.
 */
export const filterPostsByPeriodLocal = (posts: any[], period: { start?: Date | null; end?: Date | null } | null | undefined): any[] => {
  const { start: periodStart, end: periodEnd } = period || {};
  if (!periodStart && !periodEnd) return posts;

  const getDateOnly = (date: Date | string | number | null | undefined): Date | null => {
    if (!date) return null;
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const postDateToDate = (postDateNum: unknown): Date | null => {
    if (!postDateNum) return null;
    const postDateStr = String(postDateNum);
    if (postDateStr.length === 8) {
      const year = parseInt(postDateStr.substring(0, 4), 10);
      const month = parseInt(postDateStr.substring(4, 6), 10) - 1;
      const day = parseInt(postDateStr.substring(6, 8), 10);
      const dateObj = new Date(year, month, day);
      if (!Number.isNaN(dateObj.getTime())) {
        return dateObj;
      }
    }
    return null;
  };

  const periodStartDateOnly = periodStart ? getDateOnly(periodStart) : null;
  const periodEndDateOnly = periodEnd ? getDateOnly(periodEnd) : null;

  return posts.filter((post: any) => {
    const postDate = post.postDate;
    if (!postDate) return false;

    const postDateObj = postDateToDate(postDate);
    if (!postDateObj) return false;

    const postDateOnly = getDateOnly(postDateObj);
    // 타입 좁히기용 · 도달 불가(바로 위 postDateObj 가드가 통과한 뒤다)
    if (!postDateOnly) return false;

    if (periodStartDateOnly && postDateOnly < periodStartDateOnly) return false;
    if (periodEndDateOnly && postDateOnly > periodEndDateOnly) return false;

    return true;
  });
};

/**
 * 캠페인 상세(`/service/operations/seeding/:id` · 관리자 목업 `…/campaign/:id`) 진입 시 그 캠페인을
 * 먼저 조회하려고 pathname 에서 id 를 뜬다.
 */
export const getDetailCampaignIdFromPathname = (pathname: unknown): string | null => {
  if (!pathname || typeof pathname !== 'string') return null;
  const parts = pathname.split('/').filter(Boolean);
  const seedingRoot = paths.service.campaign.root.split('/').filter(Boolean);
  const onSeeding = seedingRoot.every((seg, i) => parts[i] === seg);
  const idx = onSeeding ? seedingRoot.length - 1 : parts.indexOf('campaign');
  if (idx === -1) return null;
  const raw = parts[idx + 1];
  if (!raw) return null;
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
};
